/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useCallback, useState, useEffect } from "react";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import { AgentExtensions } from "../components/AgentExtensions/AgentExtensions";
import {
	getAgents,
	getGlobalSettings,
	saveGlobalSettings,
	syncAgents,
	updateAgents,
	getAgentById,
} from "../services/AgentExtensionsService";
import { redactPII } from "../../../utility/helpers";
import { PII_FIELDS } from "../../../utility/constants";
import { ProgressOverlay, ShowSnackbar } from "../../../components/Common";

const COMPONENT_NAME = "Admin settingsManager/pages/AgentExtensionsPage";

/**
 * Page used to manage agents extension.
 * Contains functionality to update agents extension.
 */
const AgentExtensionsPage = () => {
	const [notification, setNotification] = React.useState({ message: "", severity: "success" });
	const [isLoading, setIsLoading] = useState(false);
	const [isAgentLoading, setIsAgentLoading] = useState(false);
	const [agents, setAgents] = useState(null);
	const [globalSettings, setGlobalSettings] = useState({});
	const handleSnackBarClose = () => {
		setNotification((cur) => ({ ...cur, message: "" }));
	};
	const setSnackbar = (message, severity) => {
		setNotification({ message, severity });
	};

	const fetchAgents = useCallback(async () => {
		try {
			const options = {
				extension: "",
				transcribeVoicemail: false,
				encryptVoicemail: false,
				deliveryOptions: {
					sms: {
						enabled: false,
						phoneNumber: "",
					},
					email: false,
				},
			};
			setIsLoading(true);
			const response = await getAgents();
			const agents = [];
			response.forEach((agent) => {
				agents.push({ ...options, ...agent });
			});
			agents.sort((a, b) => (a.username.toLowerCase() > b.username.toLowerCase() ? 1 : -1));
			setAgents(agents);
		} catch (error) {
			v2.error(`Error while fetching agents`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.GET_AGENTS_API_ERROR,
			});
			setAgents(null);
			setSnackbar("Failed to load agents.", "error");
		} finally {
			setIsLoading(false);
		}
	}, []);

	const fetchGlobalSettings = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await getGlobalSettings();
			setGlobalSettings(response.settings || {});
		} catch (error) {
			v2.error(`Error while fetching global settings.`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.GET_GLOBAL_SETTINGS_API_ERROR,
			});
			setGlobalSettings({});
		} finally {
			setIsLoading(false);
		}
	}, []);

	const fetchAgentById = useCallback(async (agentId) => {
		try {
			setIsAgentLoading(true);
			const response = await getAgentById(agentId);
			return response;
		} catch (error) {
			v2.error(`Error while fetching agent by agentId.`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.GET_GLOBAL_SETTINGS_API_ERROR,
			});
		} finally {
			setIsAgentLoading(false);
		}
	}, []);

	const saveGlobalSetting = useCallback(
		async (settings) => {
			try {
				setIsLoading(true);
				await saveGlobalSettings(settings);
				setSnackbar("Successfully saved global settings.", "success");
			} catch (error) {
				v2.error(`Error while saving global settings`, {
					additionalData: { error: errorToObject(error), settings },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.UPDATE_EXTERNAL_CONTACTS_ERROR,
				});
				setSnackbar("Failed to save global settings.", "error");
			} finally {
				await fetchGlobalSettings();
				setIsLoading(false);
			}
		},
		[fetchGlobalSettings],
	);

	const syncAgentsList = useCallback(async () => {
		try {
			setIsLoading(true);
			await syncAgents();
			setSnackbar("Successfully loaded agents.", "success");
		} catch (error) {
			v2.error(`Error while sync agents.`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.SYNC_AGENTS_API_ERROR,
			});
			setSnackbar("Failed to sync agents.", "error");
		} finally {
			setIsLoading(false);
			await fetchAgents();
		}
	}, [fetchAgents]);

	const saveAgents = useCallback(
		async (agentId, agent) => {
			try {
				if (agent.deliverSMS === false) {
					agent.deliverSMSPhoneNumber = null;
				}
				setIsLoading(true);
				await updateAgents(agentId, agent);
				setSnackbar("Successfully saved agents data.", "success");
			} catch (error) {
				v2.error(`Error while saving agents data.`, {
					additionalData: { error: errorToObject(error), agent: redactPII(agent, PII_FIELDS.agent) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.UPDATE_AGENTS_API_ERROR,
				});
				setSnackbar("Failed to save agents data.", "error");
			} finally {
				setIsLoading(false);
				await fetchAgents();
			}
		},
		[fetchAgents],
	);

	useEffect(() => {
		fetchAgents();
		fetchGlobalSettings();
	}, [fetchAgents, fetchGlobalSettings]);

	return (
		<div style={{ width: "100%", marginTop: 30 }}>
			<ShowSnackbar message={notification.message} severity={notification.severity} onClose={handleSnackBarClose} />
			{(isAgentLoading || isLoading) && <ProgressOverlay />}
			<AgentExtensions
				agents={agents}
				setIsLoading={setIsLoading}
				globalSettings={globalSettings}
				saveGlobalSetting={saveGlobalSetting}
				syncAgentsList={syncAgentsList}
				handleSave={saveAgents}
				isAgentLoading={isAgentLoading}
				fetchAgentById={fetchAgentById}
			/>
		</div>
	);
};

export default AgentExtensionsPage;
